import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router";
import { TextField, Typography, Switch, Box, Grid, Backdrop, Card } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { useFormik } from "formik";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import { IAddGateEntery } from "src/common/typings/vehicle";
import { showNotification } from "src/common/utils/app.util";
import UseWindowDimensions from "src/common/utils/getWindowDimensions";
import CommonAutoCompleteField from "../../../../../../components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonButton from "../../../../../../components/CommonButton/CommonButton";
import CommonSelect from "../../../../../../components/molecules/CommonSelect";
import { Prompt } from "react-router-dom";
import {
  colorCodeListForGateIn,
  customColorCodeList,
  importerExporterVals,
  travellingDocTypes,
} from "src/common/utils/app.dropdowns";
import { SelectChangeEvent } from "@mui/material";
import InputMask from "react-input-mask";
import { useStyles } from "./styles";
import { validationSchema } from "./validations";
import {
  selectAllYardsListing,
  selectAllYardRowsListing,
  selectOnlyAvailableAllSlotsByRowId,
  loader,
} from "src/app-redux/yard/yardSlice";
import {
  getActiveAllYardsListingAction,
  getAllRowsByYardIdAction,
  getAllAvailableSlotsByRowIdAction,
} from "src/app-redux/yard/actions/yardActions";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import MultiSelect2 from "src/components/molecules/MultiSelect2";
import CommonTextFieldDate from "src/components/CommonTextFieldForDate/CommonTextFieldDate";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import CommonLoader from "src/components/Loader/CommonLoader";

export interface IForm extends IAddGateEntery {}

interface Props {
  handleSubmit: (formData: IForm) => Promise<void>;
  updateSubmit: (formData: IForm) => Promise<void>;
  initialValues?: any;
  vehicleConfiguration?: any;
}

export function VehicleDriverDetailsInputs({
  initialValues,
  handleSubmit,
  updateSubmit,
  vehicleConfiguration,
}: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = React.useState(false);

  const { id } = useParams<{ id: any }>();
  const [updateCase, setUpdateCase] = React.useState<boolean>(
    Boolean(id !== 0 && id !== null && id !== undefined)
  );

  const [selectedCargoTypes, setSelectedCargoTypes] = useState<string[]>([]);
  const [gdList, setGdList] = React.useState<any>([]);
  const [consignmentType, setconsignmentType] = React.useState<any>([]);

  const formik = useFormik<IAddGateEntery>({
    enableReinitialize: updateCase,
    initialValues: {
      dateTime: initialValues?.dateTime || moment(new Date()).format("YYYY-MM-DDTHH:mm"),
      vehicleRegNumber: initialValues?.vehicleRegNumber || "",
      vehicleTypeId: initialValues?.vehicleTypeId || "",
      consignmentTypeId: initialValues?.consignmentTypeId || "",
      consignmentTypeName:
        consignmentType.find((st: any) => st.id === initialValues?.consignmentTypeId)?.name || "",
      cargoTypeId: "21",
      slipNumber: initialValues?.slipNumber || "",
      driverName: initialValues?.driverName || "",
      travellingDocType: initialValues?.travellingDocType || "Passport",
      travellingDocNumber: initialValues?.travellingDocNumber || "",
      crmNumber: initialValues?.crmNumber || "",
      eFormNumber: initialValues?.eFormNumber || "",
      locationInTerminal: initialValues?.locationInTerminal || "",
      vehicleStatusId: initialValues?.vehicleStatusId || "",
      gdDate: initialValues?.gdDate ? moment(initialValues.gdDate).format("YYYY-MM-DD") : "",
      driverFatherName: initialValues?.driverFatherName || "",
      sealNo: initialValues?.sealNo || "",
      containerNumber: initialValues?.containerNumber || "OPEN TRUCK",
      commercial: initialValues?.commercial ? Boolean(initialValues.commercial) : true,
      driverResident: initialValues?.driverResident || "",
      gdNo: initialValues?.gdNo || "",
      webocVehicleNumber: initialValues?.webocVehicleNumber || "",
      remarks: initialValues?.remarks || "",
      vehicleChassisNumber: initialValues?.vehicleChassisNumber || "",
      notes: initialValues?.notes || "",
      vehicleColor: initialValues?.vehicleColor || "",
      vehicleBrand: initialValues?.vehicleBrand || "",
      vehicleModel: initialValues?.vehicleModel || "",
      vehicleMake: initialValues?.vehicleMake || "",
      driverMobileNumber: initialValues?.driverMobileNumber || "",
      agentMobileNumber: initialValues?.agentMobileNumber || "",
      driverCNIC: initialValues?.driverCNIC || "",
      exporterName: initialValues?.exporterName || "",
      exporterPhoneNumber: initialValues?.exporterPhoneNumber || "",
      importerName: initialValues?.importerName || "",
      importerPhoneNumber: initialValues?.importerPhoneNumber || "",
      driverNTN: initialValues?.driverNTN || "",
      supplierName: initialValues?.supplierName || "",
      webBocId: initialValues?.webBocId || "",
      agentName: initialValues?.agentName || "",
      vehicleColorCode: initialValues?.vehicleColorCode || 1,
      vehicleCustomColorCode: initialValues?.vehicleCustomColorCode || 1,
      yardId: initialValues?.yardId || "",
      yardRowId: initialValues?.rowId || "",
      yardSlotId: initialValues?.slotId || "",
      passNumber: initialValues?.passNumber || "",
      cargoTypeIds: initialValues?.cargoTypeIds?.toString() || "",
      containerList: initialValues?.containerList || "",
    },
    validationSchema: validationSchema,
    onSubmit: (values, actions) => {
      if (values.vehicleMake == "" || values.vehicleMake == null) values.vehicleMake = null;

      // Ensure containerList is always a comma-separated string
      if (selectedWebocContainers && selectedWebocContainers.length > 0) {
        // Build containerList from selected containers
        const containerNumbers = selectedWebocContainers
          .map((item: any) => {
            // Extract container number from various possible property names
            return (
              item.registrationNumber ||
              item.containerNumber ||
              item.containerNo ||
              (typeof item === "string" ? item : "")
            );
          })
          .filter((item: string) => item && String(item).trim().length > 0)
          .map((item: string) => String(item).trim())
          .join(",");
        values.containerList = containerNumbers;
      } else if (values.containerList && typeof values.containerList === "string") {
        // Normalize existing containerList (remove extra spaces, filter empty values)
        values.containerList = values.containerList
          .split(",")
          .map((item: string) => item.trim())
          .filter((item: string) => item.length > 0)
          .join(",");
      } else {
        values.containerList = "";
      }

      if (id !== 0 && id !== null && id !== undefined) {
        values.gatePassId = id;
        updateSubmit({ ...values, vehicleRegNumber: values.vehicleRegNumber.toUpperCase() });
      } else {
        handleSubmit({ ...values, vehicleRegNumber: values.vehicleRegNumber.toUpperCase() });
      }
      actions.setSubmitting(false);
      setIsDirty(false);
      values.vehicleMake = "";
    },
  });
  const { height } = UseWindowDimensions(); // get browser dimension
  const minAccHeight = 50; // Right Content
  let accordionMaxHeight = 0;
  accordionMaxHeight = height - minAccHeight;
  const [submitCounter, setSubmitCounter] = useState<any>(0);
  const [vehiclestatusList, setvehicleStatusList] = React.useState<any>([]);
  const [locationAtTerminal, setlocationAtTerminal] = React.useState<any>([]);
  const [vehicleType, setvehicleType] = React.useState<any>([]);
  const [typeOfCargoArray, settypeOfCargoArray] = React.useState<any>([]);
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  const allTravellingDocTypes = travellingDocTypes;
  const multiselectRef: any = useRef();
  const [weBocVehicleID, setWeBocVehicleID] = React.useState<any>([]);
  const [gdLoading, setGdLoading] = useState(false);

  const [showVehicleNumTextField, setShowVehicleNumTextField] = React.useState<boolean>(false);
  const [vehicleRegNumberArr, setvehicleRegNumberArr] = React.useState<any[]>([
    { registrationNumber: "Add New Vehicle" },
  ]);
  const [vehicleNumber, setVehicleNumber] = useState<any>("");
  const [webocContainerOptions, setWebocContainerOptions] = useState<any[]>([]);
  const [selectedWebocContainers, setSelectedWebocContainers] = useState<any[]>([]);
  const [containerSearchTerm, setContainerSearchTerm] = useState<string>("");
  const [containerSearchLoading, setContainerSearchLoading] = useState(false);
  const [gdNumberSearchResults, setGdNumberSearchResults] = useState<any[]>([]);
  const [gdNumberSearchLoading, setGdNumberSearchLoading] = useState(false);
  const [selectedGdNumber, setSelectedGdNumber] = useState<string>("");
  const mergedWebocContainerOptions = React.useMemo(() => {
    const uniqueMap = new Map<string, any>();

    // Normalize and merge selected containers
    selectedWebocContainers.forEach((item: any) => {
      let containerValue = "";
      if (typeof item === "string") {
        containerValue = item;
      } else if (item?.registrationNumber) {
        containerValue = String(item.registrationNumber);
      } else if (item?.containerNumber) {
        containerValue = String(item.containerNumber);
      } else if (item?.containerNo) {
        containerValue = String(item.containerNo);
      }
      if (containerValue) {
        uniqueMap.set(containerValue, { registrationNumber: containerValue });
      }
    });

    // Normalize and merge container options
    webocContainerOptions.forEach((item: any) => {
      let containerValue = "";
      if (typeof item === "string") {
        containerValue = item;
      } else if (item?.registrationNumber) {
        containerValue = String(item.registrationNumber);
      } else if (item?.containerNumber) {
        containerValue = String(item.containerNumber);
      } else if (item?.containerNo) {
        containerValue = String(item.containerNo);
      }
      if (containerValue) {
        uniqueMap.set(containerValue, { registrationNumber: containerValue });
      }
    });

    return Array.from(uniqueMap.values());
  }, [selectedWebocContainers, webocContainerOptions]);

  useEffect(() => {
    if (vehicleConfiguration.vehicleStatusData?.length > 0) {
      setvehicleStatusList(vehicleConfiguration.vehicleStatusData);
      id ??
        formik.setFieldValue(
          "vehicleStatusId",
          vehicleConfiguration.vehicleStatusData.find(
            (st: any) =>
              st.name === "In-progress" ||
              st.name === "In-Progress" ||
              st.name === "Inprogress" ||
              st.name === "InProgress"
          )?.id
        );
    }
    if (vehicleConfiguration.terminalLocationData?.length > 0) {
      setlocationAtTerminal(vehicleConfiguration.terminalLocationData);
      id ??
        formik.setFieldValue(
          "locationInTerminal",
          vehicleConfiguration.terminalLocationData.find((st: any) => st.name === "Gate-entry")?.id
        );
    }
    if (vehicleConfiguration.vehicleTypeData?.length > 0)
      setvehicleType(vehicleConfiguration.vehicleTypeData);
    if (vehicleConfiguration.consignmentTypeData?.length > 0)
      setconsignmentType(vehicleConfiguration.consignmentTypeData);
    if (vehicleConfiguration.cargoTypeData?.length > 0)
      settypeOfCargoArray(vehicleConfiguration.cargoTypeData);
  }, [vehicleConfiguration]);

  useEffect(() => {
    if (initialValues.vehicleRegNumber) {
      setShowVehicleNumTextField(true);
    }

    if (initialValues.cargoTypeIds) {
      const arr: any[] = [];
      initialValues.cargoTypeIds.forEach((item: any) => {
        const type = typeOfCargoArray.find((cargo: any) => cargo.id === item);
        if (type) arr.push(type);
      });
      setSelectedCargoTypes(arr);
    }
    if (initialValues.containerList) {
      const rawValue = Array.isArray(initialValues.containerList)
        ? initialValues.containerList.join(",")
        : initialValues.containerList;
      if (typeof rawValue === "string") {
        const containerNumbers = rawValue
          .split(",")
          .map((item: string) => item.trim())
          .filter((item: string) => item);
        const formattedContainers = containerNumbers.map((registrationNumber: string) => ({
          registrationNumber,
        }));
        setSelectedWebocContainers(formattedContainers);
      }
    }
  }, [initialValues]);

  const searchGdNumbers = useCallback(async (searchTerm: string) => {
    if (!searchTerm || searchTerm.length < 2) {
      setGdNumberSearchResults([]);
      return;
    }
    try {
      setGdNumberSearchLoading(true);
      const result = await fetchWrapper.get(`search-gd-number/${searchTerm}`);
      if (result.isSuccess && result.value?.gdList) {
        const mapped = result.value.gdList.map((item: any) => ({
          gdNumber: item.gdNumber,
        }));
        setGdNumberSearchResults(mapped);
      } else {
        setGdNumberSearchResults([]);
      }
    } catch (error) {
      setGdNumberSearchResults([]);
    } finally {
      setGdNumberSearchLoading(false);
    }
  }, []);

  const getGdContainers = async (gdNo: string) => {
    if (!gdNo) {
      setWebocContainerOptions([]);
      return;
    }
    try {
      setContainerSearchLoading(true);
      const containerResult = await fetchWrapper.get(`get-gd-containers/${gdNo}`);
      if (containerResult.isSuccess) {
        const list = containerResult.value?.containerList || [];
        const mapped = list
          .map((x: any) => {
            // Handle different possible property names and ensure we get a string value
            let containerValue: any = "";
            if (typeof x === "string") {
              containerValue = x;
            } else if (typeof x === "object" && x !== null) {
              // Try to extract string value from various possible properties
              const possibleValue =
                x.containerNumber || x.containerNo || x.registrationNumber || "";
              // Ensure we have a primitive string, not an object
              containerValue =
                typeof possibleValue === "string"
                  ? possibleValue
                  : typeof possibleValue === "object" && possibleValue !== null
                  ? JSON.stringify(possibleValue)
                  : String(possibleValue || "");
            }
            // Final check to ensure we have a valid string
            const finalValue =
              typeof containerValue === "string"
                ? containerValue.trim()
                : String(containerValue || "").trim();

            return {
              registrationNumber: finalValue,
            };
          })
          .filter((item: any) => item.registrationNumber && item.registrationNumber.length > 0); // Filter out empty values
        setWebocContainerOptions(mapped);
      } else {
        setWebocContainerOptions([]);
      }
    } catch (error) {
      setWebocContainerOptions([]);
    } finally {
      setContainerSearchLoading(false);
    }
  };

  const onMultiSelectSelection = (selectedList: any, selectedItem: any) => {
    const cargoTypes: string = selectedList.map((item: any) => (item.id ? item.id : "")).toString();
    formik.setFieldValue("cargoTypeIds", cargoTypes);
  };
  const onMultiSelectRemove = (selectedList: any, removedItem: any) => {
    const cargoTypes: string = selectedList.map((item: any) => (item.id ? item.id : "")).toString();
    formik.setFieldValue("cargoTypeIds", cargoTypes);
  };

  useEffect(() => {
    if (formik.isValid === false) {
      showNotification("Please fill complete form.", "error", dispatch);
    }
    if (submitCounter) {
      formik.submitForm();
    }
  }, [submitCounter]);

  // Slots Record
  const yardLoading = useAppSelector(loader);
  const allYardsList: any = useAppSelector(selectAllYardsListing);
  const allRowsList: any = useAppSelector(selectAllYardRowsListing);
  const allAvailableSlotsList: any = useAppSelector(selectOnlyAvailableAllSlotsByRowId);

  const [vehicleNumberLength, setVehicleNumberLength] = useState<number>(0);

  const [allYards, setAllYards] = React.useState<any[]>([]);
  const [allRows, setAllRows] = React.useState<any[]>([]);
  const [allAvailableSlots, setAllAvailableSlots] = React.useState<any[]>([]);
  useEffect(() => {
    dispatch(getActiveAllYardsListingAction()); // get all Yards
  }, []);
  useEffect(() => {
    const option1 = [{ id: 0, name: "Select Yard Name" }];
    setAllYards(option1.concat(allYardsList));
  }, [allYardsList]);
  useEffect(() => {
    const option1 = [{ id: 0, rowName: "Select Yard Row Number" }];
    setAllRows(option1.concat(allRowsList));
  }, [allRowsList]);
  useEffect(() => {
    const option1 = [{ id: 0, columnOrder: "Select Yard Row Slot" }];
    setAllAvailableSlots(option1.concat(allAvailableSlotsList));
  }, [allAvailableSlotsList]);
  useEffect(() => {
    if (id !== 0 && id !== null && id !== undefined) {
      if (
        initialValues.yardId !== 0 &&
        initialValues.yardId !== null &&
        initialValues.yardId !== undefined
      )
        dispatch(getAllRowsByYardIdAction(initialValues.yardId)); // get all Rows by Yard Id
      if (
        initialValues.rowId !== 0 &&
        initialValues.rowId !== null &&
        initialValues.rowId !== undefined
      )
        dispatch(getAllAvailableSlotsByRowIdAction(initialValues.rowId));
    }
  }, [initialValues]);

  useEffect(() => {
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);
  function handleValueChange(e: any) {
    formik.handleChange(e);
    e.target.value || JSON.stringify(formik.values) !== JSON.stringify(formik.initialValues)
      ? setIsDirty(true)
      : setIsDirty(false);
  }

  const getVehicleByRegistrationNo = async (vehicleId: string) => {
    let results: any = [];
    const option1 = [{ registrationNumber: "Add New Vehicle" }];
    results = await fetchWrapper.get(
      `search-vehicles-by-vehicle-registration-number/${vehicleId}?allVehicles=true`
    );
    if (results.isSuccess) {
      id
        ? setvehicleRegNumberArr(results.value)
        : setvehicleRegNumberArr(option1.concat(results.value));
    }
    return;
  };

  const getVehicleDetailsByRegistrationNo = async (vehicleId: string) => {
    let results: any = [];
    setLoading(true);
    results = await fetchWrapper.get(`get-vehicle-detail-by-reg-num/${vehicleId}`);
    if (results.isSuccess) {
      formik.setValues({
        ...results.value[0],
        commercial: Boolean(results.value[0]?.commercial),
        vehicleStatusId: vehicleConfiguration.vehicleStatusData.find(
          (st: any) =>
            st.name === "In-progress" ||
            st.name === "In-Progress" ||
            st.name === "Inprogress" ||
            st.name === "InProgress"
        )?.id,
        locationInTerminal: vehicleConfiguration.terminalLocationData.find(
          (st: any) => st.name === "Gate-entry"
        )?.id,
        vehicleColorCode: results.value[0]?.vehicleColorCode || 1,
        vehicleCustomColorCode: results.value[0]?.vehicleCustomColorCode || 1,
        dateTime: moment(new Date()).format("YYYY-MM-DDTHH:mm"),
        driverCNIC: results.value[0]?.driverCNIC || "",
        driverFatherName: results.value[0]?.driverFatherName || "",
        driverMobileNumber: results.value[0]?.driverMobileNumber || "",
        agentMobileNumber: results.value[0]?.agentMobileNumber || "",
        vehicleColor: results.value[0]?.vehicleColor || "",
        agentName: results.value[0]?.agentName || "",
        exporterName: results.value[0]?.exporterName || "",
        exporterPhoneNumber: results.value[0]?.exporterPhoneNumber || "",
        importerName: results.value[0]?.importerName || "",
        importerPhoneNumber: results.value[0]?.importerPhoneNumber || "",
        passNumber: "",
        slipNumber: "",
        travellingDocType: results.value[0]?.travellingDocType || "",
        travellingDocNumber: results.value[0]?.travellingDocNumber || "",
        crmNumber: "",
        eFormNumber: "",
        cargoTypeIds: "",
        containerNumber: "",
        sealNo: "",
        gdNo: "",
        webocVehicleNumber: "",
        supplierName: "",
        webBocId: "",
        vehicleChassisNumber: results.value[0]?.vehicleChassisNumber || "",
        vehicleBrand: results.value[0]?.vehicleBrand || "",
        vehicleModel: results.value[0]?.vehicleModel || "",
        vehicleMake:
          results.value[0]?.vehicleMake && results.value[0]?.vehicleMake !== "0"
            ? results.value[0].vehicleMake
            : "",
        driverNTN: results.value[0]?.driverNTN || "",
        driverResident: results.value[0]?.driverResident || "",
      });
    }
    setLoading(false);
    return;
  };

  const addNewVehicle = async () => {
    setShowVehicleNumTextField(true);
    formik.setValues({
      ...formik.initialValues,
      vehicleStatusId: vehicleConfiguration.vehicleStatusData.find(
        (st: any) =>
          st.name === "In-progress" ||
          st.name === "In-Progress" ||
          st.name === "Inprogress" ||
          st.name === "InProgress"
      )?.id,
      locationInTerminal: vehicleConfiguration.terminalLocationData.find(
        (st: any) => st.name === "Gate-entry"
      )?.id,
      dateTime: moment(new Date()).format("YYYY-MM-DDTHH:mm"),
    });
  };

  const importExistingVehicleData = async (vehicle: any) => {
    if (vehicle.isInProgress) {
      alert("The selected vehicle is already in progress.");
    } else {
      if (vehicle && vehicle.registrationNumber !== "" && vehicle.vehicleId !== 0) {
        formik.setFieldValue("vehicleRegNumber", vehicle.registrationNumber);
        getVehicleDetailsByRegistrationNo(vehicle.registrationNumber);
      } else {
        formik.setFieldValue("vehicleRegNumber", "");
      }
    }
  };

  const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debounceSubmit = debounce(() => {
    setIsSubmitting(true);
    formik.submitForm().finally(() => {
      setIsSubmitting(false);
    });
  }, 1000);

  const debouncedSearchGdNumbers = useMemo(
    () =>
      debounce((searchTerm: string) => {
        searchGdNumbers(searchTerm);
      }, 300),
    [searchGdNumbers]
  );

  const listener = (e: any) => {
    if (
      (e.key === "s" || e.key === "S") &&
      (navigator.platform.match("Mac") ? e.metaKey : e.ctrlKey)
    ) {
      e.preventDefault();
      if (JSON.stringify(formik.initialValues) != JSON.stringify(formik.values)) {
        debounceSubmit();
      }
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [formik.values, isSubmitting]);

  const scrollContainerRef: any = useRef(null);
  const datePickerRef: any = useRef(null);
  const gddatePickerRef: any = useRef(null);
  useEffect(() => {
    const handleScroll = () => {
      // Hide the calendar when the container is scrolled
      datePickerRef?.current?.flatpickr?.close();
      gddatePickerRef?.current?.flatpickr?.close();
    };
    scrollContainerRef?.current?.addEventListener("scroll", handleScroll);
    return () => {
      scrollContainerRef?.current?.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const [supplierTypeList, setSupplierTypeList] = useState([]);

  const getListing = async () => {
    let results: any = [];
    try {
      results = await fetchWrapper.get(`get_all-supplier-details?getActiveSuppliersOnly=${true}`);
      if (results.isSuccess == true) {
        setSupplierTypeList(results.value);
      }
      return;
    } catch (error: any) {
      dispatch(
        addNotificationAction({
          message: error.message,
          options: { variant: "error" },
        })
      );
    }
  };

  const getVehicleGDs = async () => {
    const results = await fetchWrapper.post("get-all-pending-gds", {});
    if (results.isSuccess) {
      setGdList(results.value.getAllPendingGds);
    }
    return;
  };
  const getWebocVehicleNumber = async (gd: any) => {
    setGdLoading(true);

    const results = await fetchWrapper.post("get-all-vehicles-by-gd", { gdNo: gd });
    if (results.isSuccess) {
      setWeBocVehicleID(results.value.getAllVehicleNumbers);
    }
    setGdLoading(false);
    return;
  };

  useEffect(() => {
    getListing();
    getVehicleGDs();
  }, []);
  useEffect(() => {
    if (formik.values.gdNo) {
      getWebocVehicleNumber(formik.values.gdNo);
    }
  }, [formik.values.gdNo]);

  useEffect(() => {
    if (selectedGdNumber) {
      getGdContainers(selectedGdNumber);
    } else {
      setWebocContainerOptions([]);
      setSelectedWebocContainers([]);
    }
  }, [selectedGdNumber]);

  return (
    <>
      {/* If prompt, when navigating to another page inside the app */}
      <Prompt when={isDirty} message={"Changes you made may not be saved."} />

      <form onSubmit={formik.handleSubmit}>
        <Backdrop className={classes.backdrop} open={loading || yardLoading || gdLoading}>
          <CommonLoader />
        </Backdrop>
        <Grid container direction="row" alignItems="flex-start">
          {/* First Section */}
          <>
            <Grid
              item
              md={12}
              component={Card}
              className={classes.tabCardContent}
              style={{ height: accordionMaxHeight }}
              ref={scrollContainerRef}
            >
              <Grid className={classes.UserMainPanel}>
                <Grid className={classes.formRow}>
                  <Grid item xs={12}>
                    <Box textAlign="right" mb={2} mt={1}>
                      <CommonButton
                        cssClass="customSubmitBtn"
                        onClick={() => {
                          formik.resetForm();
                          if (updateCase) {
                            formik.setValues({
                              ...formik.initialValues,
                            });
                          } else {
                            setShowVehicleNumTextField(false);
                            formik.setValues({
                              ...formik.initialValues,
                              vehicleStatusId: vehicleConfiguration.vehicleStatusData.find(
                                (st: any) =>
                                  st.name === "In-progress" ||
                                  st.name === "In-Progress" ||
                                  st.name === "Inprogress" ||
                                  st.name === "InProgress"
                              )?.id,
                              locationInTerminal: vehicleConfiguration.terminalLocationData.find(
                                (st: any) => st.name === "Gate-entry"
                              )?.id,
                              dateTime: moment(new Date()).format("YYYY-MM-DDTHH:mm"),
                              vehicleMake: "",
                              gdDate: "",
                            });
                            const current = multiselectRef?.current;
                            if (current) {
                              current?.getUpdated();
                            }
                          }
                        }}
                      >
                        Reset
                      </CommonButton>
                      <CommonButton
                        isGreen
                        cssClass="customSubmitBtn"
                        disable={
                          id &&
                          JSON.stringify(formik.initialValues) == JSON.stringify(formik.values)
                        }
                        onClick={() => {
                          setSubmitCounter((prev: any) => prev + 1);
                        }}
                      >
                        Save
                      </CommonButton>
                    </Box>
                  </Grid>
                </Grid>
                <Grid container direction="row" alignItems="center" spacing={2}>
                  {/* Gate-entry Details Section */}
                  <Grid
                    item
                    md={12}
                    className={`${classes.sectionHeading} ${classes.sectionHeadingFirstSec}`}
                  >
                    <Typography>Gate-Entry Details:</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <CommonTextFieldDate
                      cssClassName={`${classes.dateTime} ${"customTextField"}`}
                      outerCss={true}
                      datePickerRef={datePickerRef}
                      label={"Entry Date and Time"}
                      name={"dateTime"}
                      required={true}
                      value={formik.values.dateTime}
                      onChange={(e: any) => {
                        formik.setFieldValue("dateTime", e);
                      }}
                      maxDate={moment().format("YYYY-MM-DDTHH:mm")}
                      isReadOnly={updateCase}
                      error={formik.touched.dateTime && Boolean(formik.errors.dateTime)}
                      helperText={formik.touched.dateTime && formik.errors.dateTime}
                    />
                    {/* <Box>
                      <TextField
                        className={`${classes.dateTime} ${"customTextField"}`}
                        required
                        variant="outlined"
                        id="dateTime"
                        label="Entry Date and Time"
                        InputProps={{
                          inputProps: { max: moment(new Date()).format("YYYY-MM-DDTHH:mm") || "" },
                        }}
                        type="datetime-local"
                        defaultValue={formik.values.dateTime}
                        onChange={(event: any) => {
                          formik.setFieldValue("dateTime", event.target.value);
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        disabled={updateCase}
                      />
                    </Box> */}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box style={{ display: showVehicleNumTextField ? "none" : "block" }}>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        label="Vehicle Number"
                        placeholder="Enter Vehicle Number"
                        name="vehicleRegNumber"
                        optionsType={vehicleRegNumberArr}
                        getOptionLabel={(option) => option.registrationNumber}
                        required
                        value={
                          vehicleRegNumberArr.find(
                            (st: any) => st.registrationNumber === formik.values.vehicleRegNumber
                          ) || null
                        }
                        onChange={(option: any) => {
                          if (option && option.registrationNumber === "Add New Vehicle") {
                            addNewVehicle();
                          } else {
                            importExistingVehicleData(option);
                          }
                        }}
                        onKeyUp={(e: any) => {
                          setVehicleNumberLength(e.target.value.length);
                          setVehicleNumber(e.target.value);

                          if (e.target.value.length == 2 && vehicleNumberLength < 2) {
                            getVehicleByRegistrationNo(e.target.value);
                            return;
                          }
                        }}
                        onKeyDownCapture={(e: any) => {
                          if (/[^A-Za-z0-9\d]/.test(e.key)) e.preventDefault();
                          else handleValueChange(e);
                        }}
                        onPaste={(e: any) => {
                          if (/[^A-Za-z0-9\d]/.test(e.clipboardData.getData("text"))) {
                            e.preventDefault();
                            return;
                          }

                          if (e.clipboardData.getData("text").length >= 2)
                            getVehicleByRegistrationNo(e.clipboardData.getData("text"));
                        }}
                        error={
                          formik.touched.vehicleRegNumber && Boolean(formik.errors.vehicleRegNumber)
                        }
                        helperText={
                          formik.touched.vehicleRegNumber && formik.errors.vehicleRegNumber
                        }
                      />
                    </Box>
                    <Box style={{ display: showVehicleNumTextField ? "block" : "none" }}>
                      <TextField
                        className={"customTextField"}
                        required
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Vehicle Number"
                        name="vehicleRegNumber"
                        id="vehicleRegNumber"
                        placeholder="Enter Vehicle Number"
                        disabled={Boolean(id !== 0 && id !== null && id !== undefined)}
                        value={formik.values.vehicleRegNumber.toUpperCase()}
                        onChange={(e: any) => {
                          if (/[^A-Za-z0-9\d]/.test(e.nativeEvent.data)) e.preventDefault();
                          else handleValueChange(e);
                        }}
                        onPaste={(e: any) => {
                          if (/[^A-Za-z0-9\d]/.test(e.clipboardData.getData("text")))
                            e.preventDefault();
                        }}
                        error={
                          formik.touched.vehicleRegNumber && Boolean(formik.errors.vehicleRegNumber)
                        }
                        helperText={
                          formik.touched.vehicleRegNumber && formik.errors.vehicleRegNumber
                        }
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        error={
                          formik.touched.consignmentTypeId &&
                          Boolean(formik.errors.consignmentTypeId)
                        }
                        helperText={
                          formik.touched.consignmentTypeId && formik.errors.consignmentTypeId
                        }
                        label="Type of Consignment"
                        placeholder="Select Type Of Consignment"
                        name="consignmentTypeId"
                        optionsType={consignmentType}
                        required
                        getOptionLabel={(option: any) => option.name || ""}
                        value={
                          consignmentType.find(
                            (st: any) => st.id === formik.values.consignmentTypeId
                          ) || null
                        }
                        onChange={(option: any) => {
                          if (option && option.id !== 0) {
                            formik.setFieldValue("consignmentTypeId", option.id);
                            formik.setFieldValue("consignmentTypeName", option.name);
                            handleValueChange({ target: { value: option.id } });
                          }
                        }}
                      />
                    </Box>
                  </Grid>
                  {formik.values.consignmentTypeName == "Export" && (
                    <>
                      <Grid item xs={12} md={4}>
                        <Box>
                          <CommonAutoCompleteField
                            cssClassName="customTextField"
                            error={formik.touched.gdNo && Boolean(formik.errors.gdNo)}
                            helperText={formik.touched.gdNo && formik.errors.gdNo}
                            label="Good’s Declaration Number (GD#)"
                            name="vehicleTypeId"
                            optionsType={gdList}
                            placeholder="Select GD#"
                            required={formik.values.consignmentTypeName == "Export"}
                            getOptionLabel={(option: any) => option.gdNo || ""}
                            value={gdList.find((st: any) => st.gdNo == formik.values.gdNo) || null}
                            onChange={(option: any) => {
                              formik.setFieldValue("gdNo", option.gdNo);
                              handleValueChange({ target: { value: option.gdNo } });
                              formik.setFieldValue("webocVehicleNumber", "");
                            }}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box>
                          <CommonAutoCompleteField
                            cssClassName="customTextField"
                            error={
                              formik.touched.webocVehicleNumber &&
                              Boolean(formik.errors.webocVehicleNumber)
                            }
                            helperText={
                              formik.touched.webocVehicleNumber && formik.errors.webocVehicleNumber
                            }
                            label="WeBOC Vehicle Number"
                            name="vehicleTypeId"
                            optionsType={weBocVehicleID}
                            placeholder="Select WeBOC Vehicle Number"
                            required={formik.values.consignmentTypeName == "Export"}
                            getOptionLabel={(option: any) => option.vehicleNumber || ""}
                            value={
                              weBocVehicleID.find(
                                (st: any) => st.vehicleNumber === formik.values.webocVehicleNumber
                              ) || null
                            }
                            onChange={(option: any) => {
                              if (option) {
                                formik.setFieldValue("webocVehicleNumber", option.vehicleNumber);
                                handleValueChange({ target: { value: option.vehicleNumber } });
                              }
                            }}
                          />
                        </Box>
                      </Grid>
                    </>
                  )}
                  {formik.values.consignmentTypeName === "FCL-Export" && (
                    <>
                      <Grid item xs={12} md={4} style={{ position: "relative", padding: "0 8px" }}>
                        <Box>
                          <CommonAutoCompleteField
                            cssClassName="customTextField"
                            label="GD Number"
                            placeholder="Enter GD Number to search"
                            name="fclExportGdNo"
                            optionsType={gdNumberSearchResults}
                            getOptionLabel={(option) => option.gdNumber || ""}
                            required
                            loading={gdNumberSearchLoading}
                            value={
                              gdNumberSearchResults.find(
                                (st: any) => st.gdNumber === selectedGdNumber
                              ) || null
                            }
                            onChange={(option: any) => {
                              if (option) {
                                const gdNumber = option.gdNumber || "";
                                setSelectedGdNumber(gdNumber);
                                handleValueChange({ target: { value: gdNumber } });
                              } else {
                                setSelectedGdNumber("");
                                setWebocContainerOptions([]);
                                setSelectedWebocContainers([]);
                                formik.setFieldValue("containerList", "");
                              }
                            }}
                            onKeyUp={(e: any) => {
                              const searchTerm = e.target.value;
                              // Only search after typing at least 2 characters
                              if (searchTerm.length >= 2) {
                                debouncedSearchGdNumbers(searchTerm);
                              } else {
                                setGdNumberSearchResults([]);
                                setSelectedGdNumber("");
                              }
                            }}
                            onKeyDownCapture={(e: any) => {
                              if (/[^A-Za-z0-9\d]/.test(e.key)) e.preventDefault();
                            }}
                            onPaste={(e: any) => {
                              const pasted = e.clipboardData.getData("text");
                              if (/[^A-Za-z0-9\d]/.test(pasted)) {
                                e.preventDefault();
                                return;
                              }
                              if (pasted.length >= 2) debouncedSearchGdNumbers(pasted);
                            }}
                            error={
                              formik.touched.containerList && Boolean(formik.errors.containerList)
                            }
                            helperText={formik.touched.containerList && formik.errors.containerList}
                          />
                        </Box>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={4}
                        style={{
                          top: "15px",
                          position: "relative",
                          padding: "0 8px",
                        }}
                      >
                        <Box>
                          <TextField
                            required
                            className={"customTextField"}
                            size="small"
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            onKeyDown={(event: any) => {
                              if (event.key !== "Tab") event.preventDefault();
                            }}
                            label="GD Container Number"
                            error={
                              formik.touched.containerList && Boolean(formik.errors.containerList)
                            }
                            helperText={formik.touched.containerList && formik.errors.containerList}
                          />
                          <Box
                            className={classes.multiselectConfigFileCompression}
                            style={{
                              bottom:
                                formik.touched.containerList && Boolean(formik.errors.containerList)
                                  ? "55px"
                                  : "35px",
                            }}
                          >
                            <MultiSelect2
                              allValues={mergedWebocContainerOptions}
                              selctedItems={selectedWebocContainers}
                              displayValue={"registrationNumber"}
                              placeholder="Select GD Container Number"
                              onSelect={(selectedList: any, selectedItem: any) => {
                                setSelectedWebocContainers(selectedList);
                                const containerNumbers = selectedList
                                  .map((item: any) => item.registrationNumber || "")
                                  .filter((item: string) => item)
                                  .join(",");
                                formik.setFieldValue("containerList", containerNumbers);
                                handleValueChange({ target: { value: containerNumbers } });
                              }}
                              onRemove={(selectedList: any, removedItem: any) => {
                                setSelectedWebocContainers(selectedList);
                                const containerNumbers = selectedList
                                  .map((item: any) => item.registrationNumber || "")
                                  .filter((item: string) => item)
                                  .join(",");
                                formik.setFieldValue("containerList", containerNumbers);
                                handleValueChange({ target: { value: containerNumbers } });
                              }}
                            />
                          </Box>
                        </Box>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} md={4}>
                    <Box>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        error={formik.touched.vehicleTypeId && Boolean(formik.errors.vehicleTypeId)}
                        helperText={formik.touched.vehicleTypeId && formik.errors.vehicleTypeId}
                        label="Vehicle Type"
                        name="vehicleTypeId"
                        optionsType={vehicleType}
                        placeholder="Select Vehicle Type"
                        required
                        getOptionLabel={(option: any) => option.name || ""}
                        value={
                          vehicleType.find((st: any) => st.id === formik.values.vehicleTypeId) ||
                          null
                        }
                        onChange={(option: any) => {
                          if (option && option.id !== 0) {
                            formik.setFieldValue("vehicleTypeId", option.id);
                            handleValueChange({ target: { value: option.id } });
                          }
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={4}
                    style={{
                      top: "15px",
                      position: "relative",
                      padding: "0 8px",
                    }}
                  >
                    <Box>
                      <TextField
                        required
                        className={"customTextField"}
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        onKeyDown={(event: any) => {
                          if (event.key !== "Tab") event.preventDefault();
                        }}
                        label="Type of Cargo"
                        error={formik.touched.cargoTypeIds && Boolean(formik.errors.cargoTypeIds)}
                        helperText={formik.touched.cargoTypeIds && formik.errors.cargoTypeIds}
                      />
                      <Box
                        className={classes.multiselectConfigFileCompression}
                        style={{
                          bottom:
                            formik.touched.cargoTypeIds && Boolean(formik.errors.cargoTypeIds)
                              ? "55px"
                              : "35px",
                        }}
                      >
                        <MultiSelect2
                          allValues={typeOfCargoArray}
                          selctedItems={selectedCargoTypes}
                          displayValue={"name"}
                          placeholder="Select Type of Cargo"
                          onSelect={onMultiSelectSelection}
                          onRemove={onMultiSelectRemove}
                          ref={multiselectRef}
                        />
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <TextField
                        required
                        size="small"
                        fullWidth
                        className="customTextField"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Driver Name"
                        name="driverName"
                        id="driverName"
                        placeholder="Enter Driver Name"
                        value={formik.values.driverName}
                        onChange={handleValueChange}
                        onPaste={(e: any) => {
                          if (/[^A-Za-z ]/.test(e.clipboardData.getData("text")))
                            e.preventDefault();
                        }}
                        onKeyDownCapture={(e: any) => {
                          if (/[^A-Za-z ]/.test(e.key)) e.preventDefault();
                        }}
                        error={formik.touched.driverName && Boolean(formik.errors.driverName)}
                        helperText={formik.touched.driverName && formik.errors.driverName}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        error={
                          formik.touched.travellingDocType &&
                          Boolean(formik.errors.travellingDocType)
                        }
                        helperText={
                          formik.touched.travellingDocType && formik.errors.travellingDocType
                        }
                        label="Travelling Doc Type"
                        name="travellingDocType"
                        optionsType={allTravellingDocTypes}
                        placeholder="Select Travelling Doc Type"
                        required
                        getOptionLabel={(option: any) => option || ""}
                        value={
                          allTravellingDocTypes.find(
                            (st: any) => st === formik.values.travellingDocType
                          ) || null
                        }
                        onChange={(option: any) => {
                          if (option && option !== "") {
                            formik.setFieldValue("travellingDocType", option);
                            handleValueChange({ target: { value: option } });
                          }
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <TextField
                        required
                        size="small"
                        fullWidth
                        className="customTextField"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Travelling Doc Number"
                        name="travellingDocNumber"
                        id="travellingDocNumber"
                        placeholder="Enter Travelling Doc Number"
                        value={formik.values.travellingDocNumber}
                        onChange={handleValueChange}
                        error={
                          formik.touched.travellingDocNumber &&
                          Boolean(formik.errors.travellingDocNumber)
                        }
                        helperText={
                          formik.touched.travellingDocNumber && formik.errors.travellingDocNumber
                        }
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <TextField
                        className="customTextField"
                        required
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Slip Number"
                        name="slipNumber"
                        id="slipNumber"
                        placeholder="Enter Slip Number"
                        value={formik.values.slipNumber}
                        onChange={handleValueChange}
                        error={formik.touched.slipNumber && Boolean(formik.errors.slipNumber)}
                        helperText={formik.touched.slipNumber && formik.errors.slipNumber}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <TextField
                        size="small"
                        fullWidth
                        className="customTextField"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="CRM Number"
                        name="crmNumber"
                        id="crmNumber"
                        placeholder="Enter CRM Number"
                        value={formik.values.crmNumber}
                        onChange={handleValueChange}
                        error={formik.touched.crmNumber && Boolean(formik.errors.crmNumber)}
                        helperText={formik.touched.crmNumber && formik.errors.crmNumber}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <TextField
                        size="small"
                        fullWidth
                        className="customTextField"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="E-Form Number"
                        name="eFormNumber"
                        id="eFormNumber"
                        placeholder="Enter E-Form Number"
                        value={formik.values.eFormNumber}
                        onChange={handleValueChange}
                        error={formik.touched.eFormNumber && Boolean(formik.errors.eFormNumber)}
                        helperText={formik.touched.eFormNumber && formik.errors.eFormNumber}
                      />
                    </Box>
                  </Grid>

                  {/* Vehicle Details Section */}
                  <Grid item md={12} className={classes.sectionHeading}>
                    <Typography>Vehicle Details:</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        error={
                          formik.touched.vehicleStatusId && Boolean(formik.errors.vehicleStatusId)
                        }
                        helperText={formik.touched.vehicleStatusId && formik.errors.vehicleStatusId}
                        label="Vehicle Status"
                        placeholder="Select Vehicle Status"
                        name="vehicleStatusId"
                        optionsType={vehiclestatusList}
                        getOptionLabel={(option) => option.name}
                        value={
                          vehiclestatusList.find(
                            (st: any) => st.id === formik.values.vehicleStatusId
                          ) || null
                        }
                        onChange={(option: any) => {
                          if (option && option.id !== 0) {
                            formik.setFieldValue("vehicleStatusId", option.id);
                            handleValueChange({ target: { value: option.id } });
                          }
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        error={
                          formik.touched.locationInTerminal &&
                          Boolean(formik.errors.locationInTerminal)
                        }
                        helperText={
                          formik.touched.locationInTerminal && formik.errors.locationInTerminal
                        }
                        label="Location at Terminal"
                        placeholder="Select Location at Terminal"
                        name="locationInTerminal"
                        optionsType={locationAtTerminal}
                        getOptionLabel={(option: any) => option.name || ""}
                        value={
                          locationAtTerminal.find(
                            (st: any) => st.id === formik.values.locationInTerminal
                          ) || null
                        }
                        onChange={(option: any) => {
                          if (option && option.id !== 0) {
                            formik.setFieldValue("locationInTerminal", option.id);
                            handleValueChange({ target: { value: option.id } });
                          }
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <TextField
                        className="customTextField"
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Pass Number"
                        name="passNumber"
                        id="passNumber"
                        placeholder="Enter Pass Number"
                        disabled={false}
                        value={formik.values.passNumber}
                        onChange={handleValueChange}
                        error={formik.touched.passNumber && Boolean(formik.errors.passNumber)}
                        helperText={formik.touched.passNumber && formik.errors.passNumber}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(formik.values.commercial)}
                            onChange={handleValueChange}
                            name="commercial"
                            color="primary"
                          />
                        }
                        label="Commercial"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <TextField
                        className="customTextField"
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Chassis Number"
                        name="vehicleChassisNumber"
                        id="vehicleChassisNumber"
                        placeholder="Enter Chassis Number"
                        disabled={false}
                        value={formik.values.vehicleChassisNumber}
                        onChange={handleValueChange}
                        error={
                          formik.touched.vehicleChassisNumber &&
                          Boolean(formik.errors.vehicleChassisNumber)
                        }
                        helperText={
                          formik.touched.vehicleChassisNumber && formik.errors.vehicleChassisNumber
                        }
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <TextField
                        size="small"
                        fullWidth
                        className="customTextField"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Vehicle Color"
                        name="vehicleColor"
                        id="vehicleColor"
                        placeholder="Enter Vehicle Color"
                        disabled={false}
                        value={formik.values.vehicleColor}
                        onChange={handleValueChange}
                        // onPaste={(e: any) => {
                        //   if (/[^A-Za-z ]/.test(e.clipboardData.getData("text")))
                        //     e.preventDefault();
                        // }}
                        // onKeyDownCapture={(e: any) => {
                        //   if (/[^A-Za-z ]/.test(e.key)) e.preventDefault();
                        // }}
                        error={formik.touched.vehicleColor && Boolean(formik.errors.vehicleColor)}
                        helperText={formik.touched.vehicleColor && formik.errors.vehicleColor}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <TextField
                        //
                        size="small"
                        fullWidth
                        className="customTextField"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Vehicle Brand"
                        name="vehicleBrand"
                        id="vehicleBrand"
                        placeholder="Enter Vehicle Brand"
                        disabled={false}
                        value={formik.values.vehicleBrand}
                        onChange={handleValueChange}
                        onPaste={(e: any) => {
                          if (/[^A-Za-z0-9- ]/.test(e.clipboardData.getData("text")))
                            e.preventDefault();
                        }}
                        onKeyDownCapture={(e: any) => {
                          if (/[^A-Za-z0-9- ]/.test(e.key)) e.preventDefault();
                        }}
                        error={formik.touched.vehicleBrand && Boolean(formik.errors.vehicleBrand)}
                        helperText={formik.touched.vehicleBrand && formik.errors.vehicleBrand}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <TextField
                        //
                        size="small"
                        fullWidth
                        className="customTextField"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Vehicle Model"
                        name="vehicleModel"
                        id="vehicleModel"
                        placeholder="Enter Vehicle Model"
                        disabled={false}
                        value={formik.values.vehicleModel}
                        onChange={handleValueChange}
                        // onPaste={(e: any) => {
                        //   if (/[^A-Za-z0-9 ]/.test(e.clipboardData.getData("text")))
                        //     e.preventDefault();
                        // }}
                        // onKeyDownCapture={(e: any) => {
                        //   if (/[^A-Za-z0-9 ]/.test(e.key)) e.preventDefault();
                        // }}
                        error={formik.touched.vehicleModel && Boolean(formik.errors.vehicleModel)}
                        helperText={formik.touched.vehicleModel && formik.errors.vehicleModel}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <InputMask
                        mask="9999"
                        value={formik.values.vehicleMake}
                        disabled={false}
                        onChange={handleValueChange}
                      >
                        {() => (
                          <TextField
                            size="small"
                            fullWidth
                            className="customTextField"
                            variant="outlined"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            label="Vehicle Make/Year"
                            name="vehicleMake"
                            id="vehicleMake"
                            placeholder="2002"
                            type="text"
                            error={formik.touched.vehicleMake && Boolean(formik.errors.vehicleMake)}
                            helperText={formik.touched.vehicleMake && formik.errors.vehicleMake}
                          />
                        )}
                      </InputMask>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <TextField
                        //
                        size="small"
                        fullWidth
                        className="customTextField"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Agent Name"
                        name="agentName"
                        id="agentName"
                        placeholder="Enter Agent Name"
                        disabled={false}
                        value={formik.values.agentName}
                        onChange={handleValueChange}
                        onPaste={(e: any) => {
                          if (/[^A-Za-z ]/.test(e.clipboardData.getData("text")))
                            e.preventDefault();
                        }}
                        onKeyDownCapture={(e: any) => {
                          if (/[^A-Za-z ]/.test(e.key)) e.preventDefault();
                        }}
                        error={formik.touched.agentName && Boolean(formik.errors.agentName)}
                        helperText={formik.touched.agentName && formik.errors.agentName}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <InputMask
                        mask="+99-999-999-9999"
                        value={formik.values.agentMobileNumber}
                        disabled={false}
                        onChange={handleValueChange}
                      >
                        {() => (
                          <TextField
                            size="small"
                            fullWidth
                            className="customTextField"
                            variant="outlined"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            label="Agent’s Mobile Number"
                            name="agentMobileNumber"
                            id="agentMobileNumber"
                            placeholder="+92-123-456-7890"
                            type="text"
                            error={
                              formik.touched.agentMobileNumber &&
                              Boolean(formik.errors.agentMobileNumber)
                            }
                            helperText={
                              formik.touched.agentMobileNumber && formik.errors.agentMobileNumber
                            }
                          />
                        )}
                      </InputMask>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <CommonSelect
                      cssClassName="customTextField"
                      inputLabel="Vehicle Channel"
                      name="vehicleColorCode"
                      allValues={colorCodeListForGateIn}
                      value={formik.values.vehicleColorCode}
                      onChange={(event: SelectChangeEvent) => {
                        formik.setFieldValue("vehicleColorCode", event?.target?.value);
                        handleValueChange({ target: { value: event?.target?.value } });
                      }}
                      // {/* Commented out before UAT */}
                      // style={{
                      //   background: colorCodeListForGateIn.find(
                      //     (item) => item.value === formik.values.vehicleColorCode
                      //   )?.backgroundColor,
                      //   color: colorCodeListForGateIn.find(
                      //     (item) => item.value === formik.values.vehicleColorCode
                      //   )?.textColor,
                      //   fontWeight: 600,
                      // }}
                      // {/* Commented out before UAT */}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <CommonSelect
                      cssClassName="customTextField"
                      inputLabel="WeBOC RMS Color Code"
                      name="vehicleCustomColorCode"
                      allValues={customColorCodeList}
                      value={formik.values.vehicleCustomColorCode}
                      onChange={(event: SelectChangeEvent) => {
                        formik.setFieldValue("vehicleCustomColorCode", event?.target?.value);
                        handleValueChange({ target: { value: event?.target?.value } });
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        error={formik.touched.yardId && Boolean(formik.errors.yardId)}
                        helperText={formik.touched.yardId && formik.errors.yardId}
                        label="Yard Name"
                        placeholder="Select Yard Name"
                        name="yardId"
                        optionsType={allYards}
                        getOptionLabel={(option: any) => option.name || ""}
                        value={allYards.find((st: any) => st.id === formik.values.yardId) || null}
                        onChange={(option: any) => {
                          if (option && option.id !== 0) {
                            dispatch(getAllRowsByYardIdAction(option.id));
                            formik.setFieldValue("yardId", option.id);
                            handleValueChange({ target: { value: option.id } });
                          } else {
                            formik.setFieldValue("yardId", "");
                            formik.setFieldValue("yardRowId", "");
                            formik.setFieldValue("yardSlotId", "");
                            setAllRows([]);
                            setAllAvailableSlots([]);
                          }
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        error={formik.touched.yardRowId && Boolean(formik.errors.yardRowId)}
                        helperText={formik.touched.yardRowId && formik.errors.yardRowId}
                        label="Yard Row Number"
                        placeholder="Select Yard Row Number"
                        name="yardRowId"
                        optionsType={allRows}
                        getOptionLabel={(option: any) => option.rowName || ""}
                        value={
                          allRows.find((st: any) => st.yardRowId === formik.values.yardRowId) ||
                          null
                        }
                        onChange={(option: any) => {
                          if (option && option.id !== 0) {
                            dispatch(getAllAvailableSlotsByRowIdAction(option.yardRowId));
                            formik.setFieldValue("yardRowId", option.yardRowId);
                            handleValueChange({ target: { value: option.yardRowId } });
                          } else {
                            formik.setFieldValue("yardRowId", "");
                            formik.setFieldValue("yardSlotId", "");
                            setAllAvailableSlots([]);
                          }
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        error={formik.touched.yardSlotId && Boolean(formik.errors.yardSlotId)}
                        helperText={formik.touched.yardSlotId && formik.errors.yardSlotId}
                        label="Yard Row Slot"
                        placeholder="Select Yard Row Slot"
                        name="yardSlotId"
                        optionsType={allAvailableSlots}
                        getOptionLabel={(option: any) => option.columnOrder.toString() || ""}
                        value={
                          allAvailableSlots.find(
                            // on Add Page Show only Available Slots
                            (st: any) => st.id === formik.values.yardSlotId
                          ) || null
                        }
                        onChange={(option: any) => {
                          option && option.id !== 0
                            ? formik.setFieldValue("yardSlotId", option.id)
                            : formik.setFieldValue("yardSlotId", "");
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Driver Details Section */}
                  <Grid item md={12} className={classes.sectionHeading}>
                    <Typography>Driver’s Details:</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <TextField
                        size="small"
                        fullWidth
                        className="customTextField"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Driver's Father Name"
                        name="driverFatherName"
                        id="driverFatherName"
                        placeholder="Enter Driver's Father Name"
                        disabled={false}
                        value={formik.values.driverFatherName}
                        onChange={handleValueChange}
                        onPaste={(e: any) => {
                          if (/[^A-Za-z ]/.test(e.clipboardData.getData("text")))
                            e.preventDefault();
                        }}
                        onKeyDownCapture={(e: any) => {
                          if (/[^A-Za-z ]/.test(e.key)) e.preventDefault();
                        }}
                        error={
                          formik.touched.driverFatherName && Boolean(formik.errors.driverFatherName)
                        }
                        helperText={
                          formik.touched.driverFatherName && formik.errors.driverFatherName
                        }
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <InputMask
                        mask="+99-999-999-9999"
                        value={formik.values.driverMobileNumber}
                        disabled={false}
                        onChange={handleValueChange}
                      >
                        {() => (
                          <TextField
                            size="small"
                            fullWidth
                            className="customTextField"
                            variant="outlined"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            label="Driver’s Mobile Number"
                            name="driverMobileNumber"
                            id="driverMobileNumber"
                            placeholder="+92-123-456-7890"
                            type="text"
                            error={
                              formik.touched.driverMobileNumber &&
                              Boolean(formik.errors.driverMobileNumber)
                            }
                            helperText={
                              formik.touched.driverMobileNumber && formik.errors.driverMobileNumber
                            }
                          />
                        )}
                      </InputMask>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <TextField
                        size="small"
                        fullWidth
                        className="customTextField"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Driver's CNIC"
                        name="driverCNIC"
                        id="driverCNIC"
                        placeholder="Enter Driver's CNIC"
                        disabled={false}
                        value={formik.values.driverCNIC}
                        onChange={handleValueChange}
                        onPaste={(e: any) => {
                          if (/[^A-Za-z0-9-,_ ]/.test(e.clipboardData.getData("text")))
                            e.preventDefault();
                        }}
                        onKeyDownCapture={(e: any) => {
                          if (/[^A-Za-z0-9-,_ ]/.test(e.key)) e.preventDefault();
                        }}
                        error={formik.touched.driverCNIC && Boolean(formik.errors.driverCNIC)}
                        helperText={formik.touched.driverCNIC && formik.errors.driverCNIC}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <TextField
                        size="small"
                        fullWidth
                        className="customTextField"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="NTN"
                        name="driverNTN"
                        id="driverNTN"
                        placeholder="Enter NTN"
                        disabled={false}
                        value={formik.values.driverNTN}
                        onChange={handleValueChange}
                        onPaste={(e: any) => {
                          if (/[^A-Za-z0-9- ]/.test(e.clipboardData.getData("text")))
                            e.preventDefault();
                        }}
                        onKeyDownCapture={(e: any) => {
                          if (/[^A-Za-z0-9- ]/.test(e.key)) e.preventDefault();
                        }}
                        error={formik.touched.driverNTN && Boolean(formik.errors.driverNTN)}
                        helperText={formik.touched.driverNTN && formik.errors.driverNTN}
                      />
                    </Box>
                  </Grid>
                  {/* importer */}
                  {/* <Grid item xs={12} md={6}>
                    <Box>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        label="Importer Name"
                        placeholder="Select Importer Name"
                        name="importerName"
                        optionsType={supplierTypeList.filter(
                          (option: any) => option.supplierType == "Importer"
                        )}
                        getOptionLabel={(option: any) => option.supplierName || ""}
                        renderOption={(option) => <span>{option.supplierName}</span>}
                        // required
                        value={
                          supplierTypeList.find(
                            (st: any) => st.supplierName === formik.values.importerName
                          ) || null
                        }
                        onChange={(option: any) => {
                          option && option !== ""
                            ? formik.setFieldValue("importerName", option.supplierName)
                            : "";
                        }}
                        error={formik.touched.importerName && Boolean(formik.errors.importerName)}
                        helperText={formik.touched.importerName && formik.errors.importerName}
                      />
                    </Box>
                  </Grid> */}
                  {/* <Grid item xs={12} md={6}>
                    <Box>
                      <InputMask
                        mask="+99-999-999-9999"
                        value={formik.values.importerPhoneNumber}
                        disabled={false}
                        onChange={handleValueChange}
                      >
                        {() => (
                          <TextField
                            size="small"
                            fullWidth
                            className="customTextField"
                            variant="outlined"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            label="Importer Phone Number"
                            name="importerPhoneNumber"
                            id="importerPhoneNumber"
                            placeholder="+92-123-456-7890"
                            type="text"
                            error={
                              formik.touched.importerPhoneNumber &&
                              Boolean(formik.errors.importerPhoneNumber)
                            }
                            helperText={
                              formik.touched.importerPhoneNumber &&
                              formik.errors.importerPhoneNumber
                            }
                          />
                        )}
                      </InputMask>
                    </Box>
                  </Grid> */}
                  {/* exporter */}
                  {/* <Grid item xs={12} md={6}>
                    <Box>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        label="Exporter Name"
                        placeholder="Select Exporter Name"
                        name="exporterName"
                        optionsType={supplierTypeList.filter(
                          (option: any) => option.supplierType == "Exporter"
                        )}
                        getOptionLabel={(option: any) => option.supplierName || ""}
                        renderOption={(option) => <span>{option.supplierName}</span>}
                        // required
                        value={
                          supplierTypeList.find(
                            (st: any) => st.supplierName === formik.values.exporterName
                          ) || null
                        }
                        onChange={(option: any) => {
                          option && option !== ""
                            ? formik.setFieldValue("exporterName", option.supplierName)
                            : "";
                        }}
                        error={formik.touched.exporterName && Boolean(formik.errors.exporterName)}
                        helperText={formik.touched.exporterName && formik.errors.exporterName}
                      />
                    </Box>
                  </Grid> */}
                  {/* <Grid item xs={12} md={6}>
                    <Box>
                      <InputMask
                        mask="+99-999-999-9999"
                        value={formik.values.exporterPhoneNumber}
                        disabled={false}
                        onChange={handleValueChange}
                      >
                        {() => (
                          <TextField
                            size="small"
                            fullWidth
                            className="customTextField"
                            variant="outlined"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            label="Exporter Phone Number"
                            name="exporterPhoneNumber"
                            id="exporterPhoneNumber"
                            placeholder="+92-123-456-7890"
                            type="text"
                            error={
                              formik.touched.exporterPhoneNumber &&
                              Boolean(formik.errors.exporterPhoneNumber)
                            }
                            helperText={
                              formik.touched.exporterPhoneNumber &&
                              formik.errors.exporterPhoneNumber
                            }
                          />
                        )}
                      </InputMask>
                    </Box>
                  </Grid> */}
                  <Grid item xs={12} md={4}>
                    <Box>
                      <TextField
                        size="small"
                        fullWidth
                        className="customTextField"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Resident"
                        name="driverResident"
                        id="driverResident"
                        placeholder="Enter Resident"
                        disabled={false}
                        value={formik.values.driverResident}
                        onChange={handleValueChange}
                        // onPaste={(e: any) => {
                        //   if (/[^A-Za-z ]/.test(e.clipboardData.getData("text")))
                        //     e.preventDefault();
                        // }}
                        // onKeyDownCapture={(e: any) => {
                        //   if (/[^A-Za-z ]/.test(e.key)) e.preventDefault();
                        // }}
                        error={
                          formik.touched.driverResident && Boolean(formik.errors.driverResident)
                        }
                        helperText={formik.touched.driverResident && formik.errors.driverResident}
                      />
                    </Box>
                  </Grid>

                  {/* Cargo Information Section */}
                  <Grid item md={12} className={classes.sectionHeading}>
                    <Typography>Cargo Information: </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <TextField
                        size="small"
                        fullWidth
                        required
                        className="customTextField"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Container Number"
                        name="containerNumber"
                        id="containerNumber"
                        placeholder="Enter Container Number"
                        disabled={false}
                        value={formik.values.containerNumber}
                        onChange={handleValueChange}
                        // onPaste={(e: any) => {
                        //   if (/[^A-Za-z0-9 ]/.test(e.clipboardData.getData("text")))
                        //     e.preventDefault();
                        // }}
                        // onKeyDownCapture={(e: any) => {
                        //   if (/[^A-Za-z0-9 ]/.test(e.key)) e.preventDefault();
                        // }}
                        error={
                          formik.touched.containerNumber && Boolean(formik.errors.containerNumber)
                        }
                        helperText={formik.touched.containerNumber && formik.errors.containerNumber}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <TextField
                        size="small"
                        fullWidth
                        className="customTextField"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Container Seal Number"
                        name="sealNo"
                        id="sealNo"
                        placeholder="Enter Container Seal Number"
                        disabled={false}
                        value={formik.values.sealNo}
                        // onPaste={(e: any) => {
                        //   if (/[^A-Za-z0-9 ]/.test(e.clipboardData.getData("text")))
                        //     e.preventDefault();
                        // }}
                        // onKeyDownCapture={(e: any) => {
                        //   if (/[^A-Za-z0-9 ]/.test(e.key)) e.preventDefault();
                        // }}
                        onChange={handleValueChange}
                        error={formik.touched.sealNo && Boolean(formik.errors.sealNo)}
                        helperText={formik.touched.sealNo && formik.errors.sealNo}
                      />
                    </Box>
                  </Grid>

                  {/* <Grid item xs={12} md={4}>
                    <Box>
                      <TextField
                        size="small"
                        fullWidth
                        className="customTextField"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Good’s Declaration Number (GD#)"
                        name="gdNo"
                        id="gdNo"
                        placeholder="Enter GD#"
                        disabled={false}
                        value={formik.values.gdNo}
                        onChange={handleValueChange}
                        // onPaste={(e: any) => {
                        //   if (/[^A-Za-z0-9 ]/.test(e.clipboardData.getData("text")))
                        //     e.preventDefault();
                        // }}
                        // onKeyDownCapture={(e: any) => {
                        //   if (/[^A-Za-z0-9 ]/.test(e.key)) e.preventDefault();
                        // }}
                        error={formik.touched.gdNo && Boolean(formik.errors.gdNo)}
                        helperText={formik.touched.gdNo && formik.errors.gdNo}
                      />
                    </Box>
                  </Grid> */}
                  <Grid item xs={12} md={4}>
                    <CommonTextFieldDate
                      cssClassName={`${classes.dateTime} ${"customTextField"}`}
                      outerCss={true}
                      datePickerRef={gddatePickerRef}
                      label={"GD Date"}
                      name={"gdDate"}
                      value={formik.values.gdDate}
                      onChange={(e: any) => {
                        formik.setFieldValue("gdDate", e);
                        handleValueChange({ target: { value: e } });
                      }}
                      maxDate={moment().format("YYYY-MM-DD")}
                      // isReadOnly={updateCase}
                      enableTime={false}
                      error={formik.touched.gdDate && Boolean(formik.errors.gdDate)}
                      helperText={formik.touched.gdDate && formik.errors.gdDate}
                    />
                    {/* <Box>
                      <TextField
                        variant="outlined"
                        id="gdDate"
                        label="GD Date"
                        type="date"
                        InputProps={{
                          inputProps: { max: moment(new Date()).format("YYYY-MM-DD") || "" },
                        }}
                        className={`${classes.dateTime} ${"customTextField"}`}
                        onChange={(event: any) => {
                          formik.setFieldValue("gdDate", event.target.value);
                          handleValueChange({ target: { value: event.target.value } });
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Box> */}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <TextField
                        size="small"
                        fullWidth
                        className="customTextField"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Supplier Name"
                        name="supplierName"
                        id="supplierName"
                        placeholder="Enter Supplier Name"
                        disabled={false}
                        value={formik.values.supplierName}
                        onChange={handleValueChange}
                        onPaste={(e: any) => {
                          if (/[^A-Za-z ]/.test(e.clipboardData.getData("text")))
                            e.preventDefault();
                        }}
                        onKeyDownCapture={(e: any) => {
                          if (/[^A-Za-z ]/.test(e.key)) e.preventDefault();
                        }}
                        error={formik.touched.supplierName && Boolean(formik.errors.supplierName)}
                        helperText={formik.touched.supplierName && formik.errors.supplierName}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <TextField
                        size="small"
                        fullWidth
                        className="customTextField"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="WeBOC ID (IGM#)"
                        name="webBocId"
                        id="webBocId"
                        placeholder="Enter WeBOC ID (IGM#)"
                        disabled={false}
                        value={formik.values.webBocId}
                        onChange={handleValueChange}
                        error={formik.touched.webBocId && Boolean(formik.errors.webBocId)}
                        helperText={formik.touched.webBocId && formik.errors.webBocId}
                      />
                    </Box>
                  </Grid>

                  {/* Others Section */}
                  <Grid item md={12} className={classes.sectionHeading}>
                    <Typography>Others: </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box>
                      <TextField
                        multiline
                        rows={2}
                        rowsMax={5}
                        size="small"
                        fullWidth
                        className="customTextField"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Remarks"
                        name="notes"
                        id="notes"
                        placeholder="Enter Remarks"
                        disabled={false}
                        value={formik.values.notes}
                        onChange={handleValueChange}
                        error={formik.touched.notes && Boolean(formik.errors.notes)}
                        helperText={formik.touched.notes && formik.errors.notes}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box>
                      <TextField
                        multiline
                        rows={2}
                        rowsMax={5}
                        size="small"
                        fullWidth
                        className="customTextField"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Customs Inspector Remarks"
                        name="remarks"
                        id="remarks"
                        placeholder="Enter Remarks"
                        disabled={false}
                        value={formik.values.remarks}
                        onChange={handleValueChange}
                        error={formik.touched.remarks && Boolean(formik.errors.remarks)}
                        helperText={formik.touched.remarks && formik.errors.remarks}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </>
        </Grid>
      </form>
    </>
  );
}
